import moment from 'moment';
import { v1 as uuid } from 'uuid';
import { EventDataProvider } from '../interfaces/EventDataProvider';
import Logger from '../../utils/logging/Logger';
import uaParser from 'ua-parser-js';
import { AppRoutingParams } from '../../routing/models/AppRoutingParams';
import { ConfigKey, getEnvVariable } from '@legacy-modules/common/services/EnvironmentConfigurationService';
import { lmaReduxStore } from '@redux';

type ViewEvent = {
  viewData: AppRoutingParams;
};
type LoginEvent = {
  loginInfo: {
    success: boolean;
    error: string | null;
  };
};
type TrackingEvent = ViewEvent | LoginEvent | null;

export default class TrackingService {
  _logger: Logger = Logger.getInstance('TrackingService');

  eventDataProviders: {
    [key: string]: EventDataProvider;
  } = {
    timestamp: async () => moment().toISOString(),
    source: async () => 'lma-ui',
    uuid: async () => uuid(),
    browserInfo: async () => {
      const info = uaParser(window.navigator.userAgent);
      return {
        ...info,
        language: window.navigator.language || window.navigator['userLanguage'],
      };
    },
    resolution: async () => ({
      width: window.screen.width,
      height: window.screen.height,
    }),
  };

  async _buildEvent(
    type: string,
    data: Object | null | undefined = null,
    additionalDataProvider?: {
      [key: string]: EventDataProvider;
    }
  ) {
    let event = { type: type };
    // get additional data from data providers
    let providers: {
      [key: string]: EventDataProvider;
    } = this.eventDataProviders;
    if (additionalDataProvider) {
      providers = { ...providers, ...additionalDataProvider };
    }
    const promises: Array<Promise<void>> = Object.entries(providers).map(async (kv) => {
      const key: string = kv[0];
      const eventDataProvider: EventDataProvider = kv[1];
      let eventData;
      try {
        eventData = await eventDataProvider();
      } catch (e) {
        eventData = {
          error: true,
          message: 'getting data from eventDataProvider ' + key + ' failed',
          exception: e.toString(),
        };
      }
      if (eventData) {
        event[key] = eventData;
      }
    });
    await Promise.all(promises);
    if (data) {
      event = { ...event, ...data };
    }
    return event;
  }

  async sendEvent(
    type: string,
    data: TrackingEvent = null,
    additionalDataProvider?: {
      [key: string]: EventDataProvider;
    }
  ) {
    try {
      const event = await this._buildEvent(type, data, additionalDataProvider);
      const url = new URL(`${getEnvVariable(ConfigKey.HTTP_API_URL)}/ui-tracking`);
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: lmaReduxStore.getState().auth.tokenId,
        },
        body: JSON.stringify(event),
      });
    } catch (e) {
      this._logger.error('Sending analytics event failed', type, e);
    }
  }
}
